import * as React from 'react';
import { FC, useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library';

import { IncentiveEvaluationMessages } from 'components/incentive-evaluation-messages/incentive-evaluation-messages';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { IncentiveItemsContainer, LoyaltyIncentivesGrid } from '../loyalty-incentives-components';
import { IncentiveCard } from '../loyalty-incentives-components/incentive-card/incentive-card';
import { REWARD_CARD_HEIGHT } from '../loyalty-incentives-components/incentive-card/incentive-card.styled';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import { RewardCardContent } from '../loyalty-incentives-components/incentive-card/reward-card-content';

import {
  CATEGORY_TRANSITION_TIME,
  CategoryHeader,
  CategoryImage,
  CategoryLabel,
  DelayedUnmountRewardContainer,
  RewardCategory,
  StyledIconArrowDown,
} from './loyalty-rewards.styled';
import { ILoyaltyRewardsViewProps } from './types';

/**
 * Scroll parent to get child into view.
 *
 * @param {HTMLElement | null} container: container element that has overflow: auto/scroll
 * @param {HTMLElement | null} child: child element
 */
const scrollContainerToChild = (container: HTMLElement | null, child: HTMLElement | null) => {
  if (!container || !child) {
    return;
  }
  // Fill child's immediate parent with enough empty space that allow child to be scrolled at top
  if (child.parentElement) {
    const scrollHeight =
      child.parentElement.scrollHeight - parseInt(child.parentElement.style.paddingBottom || '0');
    const padding =
      scrollHeight <= child.parentElement.offsetHeight
        ? Math.max(child.offsetTop + container.offsetHeight - scrollHeight, 0)
        : Math.max(child.offsetTop + child.parentElement.offsetHeight - scrollHeight, 0);
    child.parentElement.style.paddingBottom = `${padding}px`;
  }

  container.scrollTo({ top: child.offsetTop, behavior: 'smooth' });
};

export const LoyaltyRewardsView: FC<ILoyaltyRewardsViewProps> = ({
  handleClick,
  sanityRewardsMap,
  selectedCategories,
  setSelectedCategories,
  engineRewardsMap,
  formatMessage,
  selectedReward,
  rewardsTabPath,
  rewardCategories,
  refetchRewards,
  rewardsEvaluationMap,
  isWidget,
  setSelectedRewardIdFromWidget,
}) => {
  const rewardsRoute = `${formatMessage({ id: 'routes.loyaltyHome' })}/${rewardsTabPath}`;
  const [lastSelectedCategory, setLastSelectedCategory] = useState('');
  const lastSelectedCategoryRef = useRef<HTMLLIElement | null>();
  const [scrollTimeoutId, setScrollTimeoutId] = useState<number | null>(null);
  const lockOnImageEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOCK_ON_REWARD_IMAGE));

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const setScrollContainerRef = (e: HTMLElement | null) => {
    scrollContainerRef.current = e;
  };

  useEffect(() => {
    // When used as widget in the reward dashboard, we don't want to add/remove bottom margin
    if (isWidget) {
      return;
    }
    // A timeout with `CATEGORY_TRANSITION_TIME` is needed because after the transition ends,
    // the height of the window changes, affecting the scroll position
    const currentScrollTimeoutId = setTimeout(() => {
      if (lastSelectedCategoryRef.current && scrollContainerRef.current) {
        scrollContainerToChild(scrollContainerRef.current, lastSelectedCategoryRef.current);
      }

      setScrollTimeoutId(null);
    }, [CATEGORY_TRANSITION_TIME]);

    setScrollTimeoutId(currentScrollTimeoutId);

    return () => clearTimeout(currentScrollTimeoutId);
  }, [isWidget, lastSelectedCategory, setScrollTimeoutId]);

  return (
    <LoyaltyIncentivesGrid
      selectedIncentive={selectedReward}
      refetch={refetchRewards}
      goBack={rewardsRoute}
      engineIncentivesMap={engineRewardsMap}
      setScrollContainerRef={setScrollContainerRef}
      offersInCooldown={false}
      isWidget={isWidget}
      setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
    >
      {rewardCategories.map(category => {
        const categoryLabel = category.label?.en;
        if (!category.rewards?.length || !categoryLabel) {
          return null;
        }

        const isCategorySelected = selectedCategories.includes(categoryLabel);

        const categoryHeight = category.rewards.length * REWARD_CARD_HEIGHT;
        const onClickCategoryHeader = () => {
          if (scrollTimeoutId) {
            // This will most likely not happen, but handle the case where
            // more than 1 scroll is queued up
            clearTimeout(scrollTimeoutId);
            setScrollTimeoutId(null);
          }

          const lastSelectedCat = !isCategorySelected ? categoryLabel : '';
          setLastSelectedCategory(lastSelectedCat);

          return setSelectedCategories(selected => {
            const entries = new Set(selected);
            if (entries.has(categoryLabel)) {
              entries.delete(categoryLabel);
            } else {
              entries.add(categoryLabel);
            }
            return Array.from(entries);
          });
        };

        const isCurrentSelection = lastSelectedCategory === categoryLabel;

        return (
          <RewardCategory
            open={isCategorySelected}
            isCurrentSelection={isCurrentSelection}
            categoryHeight={categoryHeight}
            key={categoryLabel}
            ref={e => {
              if (isCurrentSelection) {
                lastSelectedCategoryRef.current = e;
              }
            }}
          >
            <IncentiveItemsContainer>
              <CategoryHeader onClick={onClickCategoryHeader}>
                {category?.image?.locale && (
                  <CategoryImage
                    alt={`${categoryLabel}-image`}
                    image={category.image.locale}
                    objectFitContain
                  />
                )}
                <CategoryLabel>{categoryLabel}</CategoryLabel>
                <StyledIconArrowDown>
                  <Icon icon="select" color="icon-default" width="24px" aria-hidden />
                </StyledIconArrowDown>
              </CategoryHeader>
              <DelayedUnmountRewardContainer
                isVisible={isCategorySelected}
                delayTime={CATEGORY_TRANSITION_TIME}
              >
                {category.rewards.map(reward => {
                  const rewardId = reward?._id;
                  const incentive = sanityRewardsMap[rewardId || ''];

                  const loyaltyRewardId = incentive?.loyaltyEngineId || '';
                  const engineReward = engineRewardsMap[loyaltyRewardId];
                  const rewardEvaluationResults = rewardsEvaluationMap[loyaltyRewardId];
                  return (
                    rewardId && (
                      <IncentiveCard
                        key={rewardId}
                        incentiveData={parseIncentiveData(incentive)}
                        handleClick={handleClick}
                        locked={lockOnImageEnabled && !!engineReward?.locked}
                      >
                        <RewardCardContent engineReward={engineReward}></RewardCardContent>
                        <IncentiveEvaluationMessages
                          incentiveEvaluationResults={rewardEvaluationResults}
                          hideIcon
                        />
                      </IncentiveCard>
                    )
                  );
                })}
              </DelayedUnmountRewardContainer>
            </IncentiveItemsContainer>
          </RewardCategory>
        );
      })}
    </LoyaltyIncentivesGrid>
  );
};
